import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, MapPin, Clock, ArrowRight, Sparkles, Loader2, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import Shell from "@/components/Shell";

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [aiQuery, setAiQuery] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [aiItems, setAiItems] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = category === "all" ? "" : `?category=${category}`;
    api.get(`/experiences${q}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [category]);

  const setCat = (c) => { setAiItems(null); setAiNote(""); setParams(c === "all" ? {} : { category: c }); };
  const activeCat = cats.find((c) => c.slug === category);

  const askAI = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post("/ai/recommend", { query: aiQuery });
      setAiItems(r.data.experiences || []);
      setAiNote(r.data.note || "");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setAiLoading(false); }
  };
  const clearAI = () => { setAiItems(null); setAiNote(""); setAiQuery(""); };

  const shown = aiItems !== null ? aiItems : items;

  return (
    <Shell>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-6">
        <span className="text-xs font-semibold uppercase tracking-widest text-accent">Explore Zatayoo</span>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-3 text-foreground">{activeCat ? activeCat.label : "All experiences"}</h1>
        <p className="text-muted-foreground mt-4 max-w-lg text-lg">{activeCat ? activeCat.tagline : "Curated hidden places, treks, spiritual journeys, stays, beaches, parties and adventure across India."}</p>

        <form onSubmit={askAI} className="mt-6 flex flex-wrap items-center gap-2" data-testid="ai-search">
          <div className="relative flex-1 min-w-[240px]">
            <Sparkles size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-accent" />
            <input value={aiQuery} onChange={(e) => setAiQuery(e.target.value)} placeholder="Ask AI: 'quiet spiritual trip in the mountains'…" data-testid="ai-search-input"
              className="w-full h-12 rounded-full border border-input bg-card pl-10 pr-4 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
          </div>
          <button type="submit" disabled={aiLoading} data-testid="ai-search-btn"
            className="h-12 rounded-full bg-primary text-primary-foreground px-6 font-semibold flex items-center gap-2 disabled:opacity-60">
            {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Ask AI
          </button>
        </form>

        {aiItems !== null && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-accent/30 bg-accent/5 p-4" data-testid="ai-note">
            <Sparkles size={18} className="text-accent shrink-0 mt-0.5" />
            <p className="text-sm text-foreground/80 flex-1">{aiNote || `Showing AI picks for "${aiQuery}".`}</p>
            <button onClick={clearAI} className="text-muted-foreground hover:text-foreground" data-testid="ai-clear"><X size={16} /></button>
          </div>
        )}

        {aiItems === null && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="explore-filters">
            <button onClick={() => setCat("all")} data-testid="filter-all"
              className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${category === "all" ? "bg-primary text-primary-foreground border-primary" : "border-primary/20 text-foreground/70 hover:bg-secondary"}`}>All</button>
            {cats.map((c) => (
              <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`filter-${c.slug}`}
                className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${category === c.slug ? "bg-primary text-primary-foreground border-primary" : "border-primary/20 text-foreground/70 hover:bg-secondary"}`}>{c.label}</button>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-secondary/60 animate-pulse" />)}</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="group flex flex-col rounded-2xl overflow-hidden border border-primary/10 bg-card" data-testid={`explore-card-${i}`}>
                <Link to={`/experience/${e.id}`} className="h-52 overflow-hidden relative block">
                  <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <span className="absolute top-3 left-3 rounded-full bg-white/90 text-primary text-xs font-semibold px-3 py-1">{e.category_label}</span>
                  {e.mode === "discovery" && <span className="absolute top-3 right-3 rounded-full bg-primary/80 text-white text-xs font-semibold px-3 py-1">Enquire</span>}
                </Link>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} /> {e.location}</div>
                  <h3 className="font-semibold text-xl mt-1.5 text-foreground">{e.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed flex-1">{e.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                    <span className="flex items-center gap-1"><Clock size={12} /> {e.duration}</span>
                    <span className="flex items-center gap-1"><Star size={12} className="fill-accent text-accent" /> {e.rating}</span>
                    {e.difficulty && <span>{e.difficulty}</span>}
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-primary/10">
                    <span className="font-display text-2xl text-foreground">{inr(e.price)}<span className="text-xs text-muted-foreground font-sans"> /person</span></span>
                    <Link to={`/experience/${e.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-px transition-transform" data-testid={`view-${i}`}>
                      {e.mode === "discovery" ? "Enquire" : "Book"} <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
            {shown.length === 0 && <p className="text-muted-foreground col-span-full text-center py-16">Nothing here yet.</p>}
          </div>
        )}
      </section>
    </Shell>
  );
}
